'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Building2, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CompanyHistoryEvent, GesellschafterItem, RegisterParticipantItem } from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { formatSharePercent } from '@/lib/helpers';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { useLocale } from '@/hooks/use-locale';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { LocaleLink } from '@/components/locale-link';
import { COMPANY_TYPE_REGEX, PERSON_TYPE_REGEX, calculateAge, formatAge } from './companyDetailUtils';

/** Normalize a date (DD.MM.YYYY or YYYY-MM-DD…) to a YYYY-MM-DD comparison key. */
function normDateKey(d: string | undefined | null): string | null {
  if (!d) return null;
  const dot = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dot) return `${dot[3]}-${dot[2]}-${dot[1]}`;
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  return d;
}

function participantDisplayName(p: RegisterParticipantItem): string {
  return (
    [p.first_name, p.last_name].filter(Boolean).join(' ') ||
    p.organization ||
    ''
  );
}

/**
 * Build standalone owner rows for CD Kommanditisten, deduped against the
 * shareholders already shown (Gesellschafterliste rows + register
 * participants — e.g. the single Kommanditist a small KG's SI already lists).
 *
 * Used both by the table and by the parent card's tab visibility / count, so
 * the two never disagree. For standalone KGs `showKommanditPercent` is false
 * and the percentage is dropped: the Komplementär's stake isn't in the CD, so
 * a % over the Kommanditeinlage alone would mislead.
 */
export function buildKommanditistenRows(
  gesellschafter: GesellschafterItem[],
  ownerParticipants: RegisterParticipantItem[],
  kommanditisten: CompanyHistoryEvent[],
  showKommanditPercent: boolean,
): GesellschafterItem[] {
  const shownNameKeys = new Set<string>([
    ...gesellschafter.map((g) => g.name.toLowerCase()),
    ...ownerParticipants.map((p) => participantDisplayName(p).toLowerCase()),
  ]);
  const shownBirthKeys = new Set<string>(
    [
      ...gesellschafter.map((g) => normDateKey(g.date_of_birth)),
      ...ownerParticipants.map((p) => normDateKey(p.date_of_birth)),
    ].filter((d): d is string => !!d),
  );

  const rows: GesellschafterItem[] = [];
  for (const evt of kommanditisten) {
    const d = evt.data ?? {};
    const isCompany = (d.member_type as string) === 'company';
    const vorname = ((d.vorname as string) ?? '').trim();
    const surname = ((d.name as string) ?? '').trim();
    const fullName = isCompany
      ? surname
      : [vorname, surname].filter(Boolean).join(' ');
    if (!fullName) continue;
    const bd = normDateKey(d.birthdate as string);
    if (bd && shownBirthKeys.has(bd)) continue;
    if (shownNameKeys.has(fullName.toLowerCase())) continue;
    shownNameKeys.add(fullName.toLowerCase());
    if (bd) shownBirthKeys.add(bd);
    rows.push({
      name: fullName,
      type: 'Kommanditist(in)',
      first_name: isCompany ? undefined : vorname || undefined,
      last_name: isCompany ? undefined : surname || undefined,
      city: (d.ort as string) ?? undefined,
      date_of_birth: isCompany
        ? undefined
        : ((d.birthdate as string) ?? undefined),
      share_percent: showKommanditPercent
        ? (d.anteil_percent as number | undefined)
        : undefined,
      shares: undefined,
      total_shares: undefined,
      gesellschafter_eu_id: undefined,
      register_court: undefined,
      register_type: undefined,
      register_id: undefined,
      logo_url: undefined,
      favicon_url: undefined,
      title: undefined,
    } as GesellschafterItem);
  }
  return rows;
}

/**
 * Table of shareholders (Gesellschafter) merged from two sources:
 *   - `gesellschafter`: parsed from the Gesellschafterliste (DK) document
 *   - `ownerParticipants`: Kommanditisten/owners from the register itself
 *
 * When available, each row is enriched with share percent / Einlage / total-Einlage pulled
 * from the CD document's historical "Kommanditisten" events using birthdate-then-name lookups
 * (including nobility-particle-stripped and fuzzy prefix matching for company names).
 *
 * Guests see only the first three rows with the third blurred + a sign-in CTA.
 */
export function GesellschafterTabContent({
  gesellschafter,
  ownerParticipants = [],
  kommanditisten = [],
  showKommanditPercent = true,
  isAuthenticated,
  euId,
}: {
  gesellschafter: GesellschafterItem[];
  ownerParticipants?: RegisterParticipantItem[];
  kommanditisten?: CompanyHistoryEvent[];
  // When false (standalone KG), Kommanditist share percentages are hidden — the
  // Komplementär's stake isn't in the CD, so any % over the Kommanditeinlage
  // alone would be misleading. Mirrors the backend's `is_standalone_kg` rule.
  showKommanditPercent?: boolean;
  isAuthenticated: boolean;
  euId: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { locale } = useLocale();

  // Convert owner participants (Kommanditist etc.) to GesellschafterItem format,
  // enriching with Kommanditeinlage data from CD aggregation when available.
  const mergedGesellschafter = useMemo<GesellschafterItem[]>(() => {
    // Normalize date to YYYY-MM-DD for comparison (handles both DD.MM.YYYY and YYYY-MM-DD)
    const normDate = (d: string | undefined | null): string | null => {
      if (!d) return null;
      const dotParts = d.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (dotParts) return `${dotParts[3]}-${dotParts[2]}-${dotParts[1]}`;
      const isoParts = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoParts) return `${isoParts[1]}-${isoParts[2]}-${isoParts[3]}`;
      return d;
    };

    // Strip nobility particles for matching
    const stripNobility = (s: string) =>
      s
        .replace(/\b(von der|von dem|von|zu|zum|zur)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();

    // Build lookups from kommanditisten for matching against register participants
    const kommByBirthdate = new Map<string, Record<string, unknown>>();
    const kommByName = new Map<string, Record<string, unknown>>();
    const kommAll: Record<string, unknown>[] = [];
    for (const evt of kommanditisten) {
      const d = evt.data ?? {};
      kommAll.push(d);
      const nd = normDate(d.birthdate as string);
      if (nd) kommByBirthdate.set(nd, d);
      // Use only first word of vorname (skip middle names) + last name for matching
      const firstName = ((d.vorname as string) ?? '').split(' ')[0];
      const fullName = [firstName, d.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (fullName) kommByName.set(fullName, d);
      // Also index without nobility particles (von Baumbach → Baumbach)
      const stripped = stripNobility(fullName);
      if (stripped && stripped !== fullName) kommByName.set(stripped, d);
      // Also index by just the name field (for companies without vorname)
      const nameOnly = ((d.name as string) ?? '').toLowerCase();
      if (nameOnly && !kommByName.has(nameOnly)) kommByName.set(nameOnly, d);
      const nameStripped = stripNobility(nameOnly);
      if (
        nameStripped &&
        nameStripped !== nameOnly &&
        !kommByName.has(nameStripped)
      )
        kommByName.set(nameStripped, d);
    }

    // Fuzzy name match for companies: participant name must be a substantial prefix of komm name
    const findKommByFuzzyName = (
      displayName: string,
    ): Record<string, unknown> | undefined => {
      const lower = displayName.toLowerCase();
      const exact = kommByName.get(lower);
      if (exact) return exact;
      // Prefix match: the shorter string must be at least 70% of the longer one
      // to avoid "Schaeffler" matching "Schaeffler Holding, LP"
      for (const d of kommAll) {
        const kommName = ((d.name as string) ?? '').toLowerCase();
        if (!kommName) continue;
        const shorter = Math.min(lower.length, kommName.length);
        const longer = Math.max(lower.length, kommName.length);
        if (shorter / longer < 0.5) continue;
        if (kommName.startsWith(lower) || lower.startsWith(kommName)) return d;
      }
      return undefined;
    };

    const fromParticipants: GesellschafterItem[] = ownerParticipants.map(
      (p) => {
        const name =
          [p.first_name, p.last_name].filter(Boolean).join(' ') ||
          p.organization ||
          '';
        const pBirthNorm = normDate(p.date_of_birth);
        const kommMatch =
          (pBirthNorm ? kommByBirthdate.get(pBirthNorm) : undefined) ??
          kommByName.get(name.toLowerCase()) ??
          kommByName.get(stripNobility(name.toLowerCase())) ??
          findKommByFuzzyName(name);

        return {
          name,
          title: p.title ?? undefined,
          first_name: p.first_name ?? undefined,
          last_name: p.last_name ?? undefined,
          type: p.role ?? '',
          city: p.city ?? undefined,
          date_of_birth: p.date_of_birth ?? undefined,
          share_percent: showKommanditPercent
            ? (kommMatch?.anteil_percent as number | undefined)
            : undefined,
          shares: showKommanditPercent
            ? (kommMatch?.einlage as number | undefined)
            : undefined,
          total_shares: showKommanditPercent
            ? (kommMatch?.total_einlage as number | undefined)
            : undefined,
          gesellschafter_eu_id: undefined,
          register_court: undefined,
          register_type: undefined,
          register_id: undefined,
          logo_url: undefined,
          favicon_url: undefined,
        } as GesellschafterItem;
      },
    );
    // Avoid duplicates: if a name already exists in gesellschafter, skip
    const existingNames = new Set(
      gesellschafter.map((g) => g.name.toLowerCase()),
    );
    const unique = fromParticipants.filter(
      (p) => !existingNames.has(p.name.toLowerCase()),
    );

    // Also enrich existing gesellschafter items that lack share data
    const enriched = gesellschafter.map((g) => {
      if (!showKommanditPercent) return g;
      if (g.share_percent != null || g.shares != null) return g;
      const gBirthNorm = normDate(g.date_of_birth);
      const kommMatch =
        (gBirthNorm ? kommByBirthdate.get(gBirthNorm) : undefined) ??
        kommByName.get(g.name.toLowerCase()) ??
        kommByName.get(stripNobility(g.name.toLowerCase())) ??
        findKommByFuzzyName(g.name);
      if (!kommMatch) return g;
      return {
        ...g,
        share_percent: kommMatch.anteil_percent as number | undefined,
        shares: kommMatch.einlage as number | undefined,
        total_shares: kommMatch.total_einlage as number | undefined,
      };
    });

    // Render CD Kommanditisten as their own owner rows (deduped against what's
    // already shown). Shared with the parent card's tab visibility / count.
    const fromKommanditisten = buildKommanditistenRows(
      gesellschafter,
      ownerParticipants,
      kommanditisten,
      showKommanditPercent,
    );

    return [...enriched, ...unique, ...fromKommanditisten];
  }, [gesellschafter, ownerParticipants, kommanditisten, showKommanditPercent]);

  const sortedGesellschafter = useMemo<GesellschafterItem[]>(() => {
    const toSharePercent = (item: GesellschafterItem): number | null => {
      if (item.share_percent != null) return item.share_percent;
      if (
        item.shares != null &&
        item.total_shares != null &&
        item.total_shares > 0
      ) {
        return (item.shares / item.total_shares) * 100;
      }
      return null;
    };
    return [...mergedGesellschafter].sort((a, b) => {
      const aPercent = toSharePercent(a);
      const bPercent = toSharePercent(b);
      if (aPercent == null && bPercent == null) return 0;
      if (aPercent == null) return 1;
      if (bPercent == null) return -1;
      return bPercent - aPercent;
    });
  }, [mergedGesellschafter]);

  const columns = useMemo<ColumnDef<GesellschafterItem>[]>(
    () => [
      {
        id: 'name',
        header: t('companyDetail.gesellschafterName'),
        cell: (info) => {
          const g = info.row.original;
          const hasLink = g.gesellschafter_eu_id && g.register_court;
          const typeLabel = g.type ?? '';
          const isPersonType =
            PERSON_TYPE_REGEX.test(typeLabel) ||
            (!COMPANY_TYPE_REGEX.test(typeLabel) && !!g.date_of_birth);
          const displayName =
            isPersonType && (g.first_name || g.last_name)
              ? [g.title, g.first_name, g.last_name].filter(Boolean).join(' ')
              : g.name;
          const LeadingIcon = isPersonType ? User : Building2;
          const iconUrl = !isPersonType
            ? getPublicStorageUrl(g.logo_url) ||
              getPublicStorageUrl(g.favicon_url)
            : null;
          return (
            <div className="flex items-start gap-1.5">
              {iconUrl ? (
                <img
                  src={iconUrl}
                  alt={`${displayName} Logo`}
                  className="h-3.5 w-3.5 shrink-0 mt-0.5 rounded-sm object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <LeadingIcon
                className={`h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5${iconUrl ? ' hidden' : ''}`}
              />
              {hasLink ? (
                <LocaleLink
                  href={generateCompanySlug(
                    {
                      name: g.name,
                      registerCourt: g.register_court,
                      registerType: g.register_type ?? undefined,
                      registerId: g.register_id ?? undefined,
                    },
                    locale,
                  )}
                  className="text-xs font-medium hover:underline break-words whitespace-normal"
                  onClick={(e) => e.stopPropagation()}
                >
                  {displayName}
                </LocaleLink>
              ) : (
                <span className="text-xs font-medium break-words whitespace-normal">
                  {displayName}
                </span>
              )}
            </div>
          );
        },
        size: 150,
      },
      {
        id: 'type',
        header: t('companyDetail.gesellschafterType'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.type || '—'}
          </span>
        ),
        size: 120,
      },
      ...(isAuthenticated
        ? [
            {
              id: 'age',
              header: t('companyDetail.age'),
              cell: (info: { row: { original: GesellschafterItem } }) => {
                const g = info.row.original;
                const age = calculateAge(g.date_of_birth) ?? null;
                return (
                  <span className="text-xs text-muted-foreground">
                    {formatAge(age, t('companyDetail.age'))}
                  </span>
                );
              },
              size: 100,
            },
          ]
        : []),
      {
        id: 'city',
        header: t('companyDetail.city'),
        cell: (info: { row: { original: GesellschafterItem } }) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.city || '—'}
          </span>
        ),
        size: 100,
      },
      {
        id: 'shares',
        header: t('companyDetail.gesellschafterShares'),
        cell: (info) => {
          const g = info.row.original;
          if (g.share_percent != null)
            return (
              <span className="text-xs">
                {formatSharePercent(g.share_percent)}
              </span>
            );
          if (
            g.shares != null &&
            g.total_shares != null &&
            g.total_shares > 0
          ) {
            return (
              <span className="text-xs">
                {formatSharePercent((g.shares / g.total_shares) * 100)}
              </span>
            );
          }
          if (g.shares != null)
            return <span className="text-xs">{g.shares}</span>;
          return <span className="text-xs text-muted-foreground">—</span>;
        },
        size: 100,
      },
    ],
    [t, isAuthenticated, locale],
  );

  // MUST be referentially stable across renders: react-table queues an
  // auto-reset microtask whenever `data` identity changes, so an inline
  // .slice() here re-renders → new array → reset → … an infinite microtask
  // loop that hard-freezes the tab (2026-07-16 company-detail freeze).
  const visibleData = useMemo(
    () =>
      isAuthenticated ? sortedGesellschafter : sortedGesellschafter.slice(0, 3),
    [isAuthenticated, sortedGesellschafter],
  );
  const hasMore = !isAuthenticated && sortedGesellschafter.length > 2;

  const table = useReactTable({
    data: visibleData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (sortedGesellschafter.length === 0) {
    if (!isAuthenticated) {
      return (
        <div className="px-5 py-3 text-xs text-muted-foreground text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'gesellschafter', euId, 'login');
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeShareholders',
              'Sign in to see shareholders',
            )}
          </a>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="relative">
      <DataGridContainer
        border={false}
        className={cn(
          'overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5',
          isAuthenticated ? 'max-h-[320px]' : '',
          !isAuthenticated &&
            '[&_tbody_tr:nth-child(3)]:blur-[5px] [&_tbody_tr:nth-child(3)]:select-none',
        )}
      >
        <DataGrid<GesellschafterItem>
          table={table}
          recordCount={visibleData.length}
          onRowClick={
            isAuthenticated
              ? (row) => {
                  if (row.gesellschafter_eu_id && row.register_court) {
                    window.location.href = generateCompanySlug(
                      {
                        name: row.name,
                        registerCourt: row.register_court,
                        registerType: row.register_type ?? undefined,
                        registerId: row.register_id ?? undefined,
                      },
                      locale,
                    );
                  }
                }
              : undefined
          }
          tableLayout={{
            headerSticky: true,
            headerBorder: true,
            rowBorder: true,
            stripped: false,
            dense: true,
            width: 'auto',
            columnsResizable: false,
            cellBorder: false,
          }}
          tableClassNames={{
            base: 'w-full',
            header: 'text-xs',
            headerRow: 'h-8 !bg-muted',
            body: 'text-xs text-foreground',
            bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
          }}
        >
          <div className="flex-1 min-h-0 overflow-auto">
            <DataGridTable />
          </div>
        </DataGrid>
      </DataGridContainer>
      {hasMore && (
        <div className="px-5 py-2 text-xs text-muted-foreground border-t border-border/40 text-center">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              trackSignupAndRedirect(pathname, 'gesellschafter', euId, 'login');
            }}
            className="text-primary hover:underline"
          >
            {t(
              'companyDetail.registerToSeeAllShareholders',
              'Sign in to see all {{count}} shareholders',
              { count: sortedGesellschafter.length },
            )}
          </a>
        </div>
      )}
    </div>
  );
}
